import { useCallback, useSyncExternalStore } from "react";
import type { MessageKey } from "../i18n/index.js";

/**
 * Light, dark, or whatever the system says (SPEC §4.6).
 *
 * "System" is the default and stamps nothing on the root, so the media query in
 * `styles.css` decides. An explicit choice stamps `data-theme`, which both the
 * media rule and its own rule are written to respect.
 *
 * Kept in `localStorage` rather than on the server: which theme somebody wants
 * is about the device in their hand, not about them. A phone used at night and
 * a laptop used at a desk can reasonably disagree.
 */

export const THEMES = ["system", "light", "dark"] as const;
export type Theme = (typeof THEMES)[number];

const KEY = "vmeste.theme";

function read(): Theme {
  try {
    const stored = localStorage.getItem(KEY);
    return THEMES.includes(stored as Theme) ? (stored as Theme) : "system";
  } catch {
    // Private mode, or storage turned off. Following the system is the right
    // thing to fall back to, not a crash.
    return "system";
  }
}

/** Put the choice on the document, or take it off for "system". */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;

  if (theme === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);

  /*
   * Keep the browser's own chrome in step.
   *
   * `theme-color` is what colours the status bar in a standalone PWA, and a
   * light bar over a dark app is the sort of thing that makes an installed app
   * feel like a web page.
   */
  const dark =
    theme === "dark" ||
    (theme === "system" && matchMedia("(prefers-color-scheme: dark)").matches);

  for (const meta of document.querySelectorAll('meta[name="theme-color"]')) {
    meta.setAttribute("content", dark ? "#14110e" : "#f2eee7");
  }
}

const listeners = new Set<() => void>();
let current: Theme = "system";
let started = false;

function start(): void {
  if (started) return;
  started = true;

  current = read();
  applyTheme(current);

  // While the choice is "system", a change to the OS setting has to reach the
  // status bar too — the CSS follows on its own, the meta tag does not.
  matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    if (current === "system") applyTheme("system");
  });
}

function subscribe(listener: () => void): () => void {
  start();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useTheme(): { theme: Theme; setTheme: (next: Theme) => void } {
  const theme = useSyncExternalStore(
    subscribe,
    () => current,
    // On a server there is no document; "system" is the honest answer.
    () => "system" as Theme,
  );

  const setTheme = useCallback((next: Theme) => {
    current = next;
    applyTheme(next);

    try {
      if (next === "system") localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, next);
    } catch {
      /* storage unavailable — the choice lasts for this session */
    }

    for (const listener of listeners) listener();
  }, []);

  return { theme, setTheme };
}

/**
 * The name of each theme, as a message key rather than as words.
 *
 * Looking the words up here, at module scope, would fix them to whichever
 * language happened to be on when this file first loaded. The key is looked up
 * where the button renders instead, so a change of language reaches it.
 */
export const THEME_LABEL: Record<Theme, MessageKey> = {
  system: "settings.themeSystem",
  light: "settings.themeLight",
  dark: "settings.themeDark",
};
